import { AlertTriangle, EyeOff, HelpCircle, Lock } from 'lucide-react'

import { cn } from 'lib/utils'

/**
 * What a block shows when it has no content to draw (MYD-495).
 *
 * Deliberately NEUTRAL, because these appear in front of an audience. A
 * restricted financial block must read "Restricted", never render an empty
 * chart that looks like zero. Sizes are logical stage pixels.
 */

interface PlaceholderProps {
	fill?: boolean
	className?: string
}

const Frame = ({
	icon: Icon,
	label,
	detail,
	fill,
	className,
}: PlaceholderProps & { icon: typeof Lock; label: string; detail?: string }) => (
	<div
		className={cn(
			'flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 px-6 text-center',
			fill && 'flex-1',
			className,
		)}
		role="note"
	>
		<Icon className="h-8 w-8 text-gray-400" aria-hidden />
		<span className="text-[20px] font-medium text-gray-500">{label}</span>
		{detail && <span className="text-[16px] text-gray-400">{detail}</span>}
	</div>
)

/** The viewer may see the asset, but not this element on it. */
export const RestrictedBlock = (props: PlaceholderProps) => <Frame icon={Lock} label="Restricted" {...props} />

/** The viewer cannot see the source asset at all. */
export const UnavailableBlock = (props: PlaceholderProps) => <Frame icon={EyeOff} label="Not available" {...props} />

/** Something is wrong with the block itself, or it failed to load. */
export const ErrorBlock = ({ message, ...props }: PlaceholderProps & { message?: string }) => (
	<Frame icon={AlertTriangle} label="Could not show this" detail={message} {...props} />
)

/** An element key this build has no renderer for (a newer server, say). */
export const UnknownBlock = (props: PlaceholderProps) => <Frame icon={HelpCircle} label="Not supported here" {...props} />

/** Still loading. */
export const LoadingBlock = ({ fill, className }: PlaceholderProps) => (
	<div
		className={cn('min-h-[120px] animate-pulse rounded-lg bg-gray-100', fill && 'flex-1', className)}
		aria-busy="true"
		aria-label="Loading"
	/>
)
